// Salaries in plain words: a summary that reads like the offer letter, a simple editor (with a "got a raise?" mode),
// and the person's details. Used on the Pay page and in Settings.

import { useMemo, useState } from 'react';
import { useApp } from '../App.jsx';
import { Dialog, Field, Icon, Money, MoneyField, MoneyInput, MonthSelect, PersonDot, Toggle, useMoneyText } from './ui.jsx';
import { monthLabel, monthName, monthOf, addMonths, dateLabel, isDate, paydayRuleLabel, payday as paydayOf, yearOf } from '../lib/calendar.js';
import { fmt, fmtPlain, parseMoney, parsePercent, r2 } from '../lib/money.js';
import { payslip, withPlanDefaults, DEFAULT_EMPLOYEE_PLAN } from '../lib/payslip.js';
import { taxParamsFor, TABLE_NAMES } from '../lib/tax/params.js';
import { jovemYear } from '../lib/tax/irsJovem.js';
import { setPlanFrom, updatePlan, updatePerson } from '../lib/actions.js';
import { newId, versionAt, PERSON_COLORS } from '../lib/state.js';

const monthOptions = Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{monthName(i + 1)}</option>);

// What a normal month (no June/December pay, full month) brings to the bank with this plan, from a month on
export function normalMonth(plan, person, taxYears, fromMonth) {
  const p = withPlanDefaults(plan);
  if (p.mode !== 'employee') return null;
  for (let i = 0; i < 14; i++) {
    const key = addMonths(fromMonth, i);
    const y = yearOf(key);
    const { params } = taxParamsFor(y, taxYears);
    const jovem = jovemYear(person.irsJovem, person.birthDate, y, params.irsJovem?.percentages, params.irsJovem?.maxAge);
    const slip = payslip(p, key, {}, { params, jovem, holidayOpts: { carnival: !!person.holidays?.carnival, extra: person.holidays?.extra || [] }, appliedAtSource: person.irsJovem?.atSource !== false });
    if (slip.netBank > 0 && slip.subsidies === 0 && !slip.notes.some((n) => n.startsWith('Part month')) && !slip.withheld.some((w) => w.adjusted)) {
      return { month: key, net: r2(slip.netBank - slip.allowance), slip, jovem };
    }
  }
  return null;
}

/* ── Summary ───────────────────────────────────────────── */

function Line({ label, value, note }) {
  return (
    <div className="salary-line">
      <span className="salary-label">{label}</span>
      <span className="salary-value">{value}</span>
      {note && <span className="salary-note">{note}</span>}
    </div>
  );
}

// The salary as the offer letter shows it, in plain words
export function SalarySummary({ person, plan: planIn = null, compact = false }) {
  const { model } = useApp();
  const current = planIn || versionAt(person.plans, model.current) || person.plans[0];
  if (!current) return <p className="muted">No income set.</p>;
  const plan = withPlanDefaults(current);
  const later = person.plans.filter((x) => x.from > (current.from || ''));
  if (plan.mode === 'none') return <p className="dim">No income for now{current.from ? ` (from ${monthLabel(current.from)})` : ''}.</p>;
  if (plan.mode === 'net') {
    return (
      <div className="salary-lines">
        <Line label="Take-home pay" value={<><Money value={plan.net} /> a month</>} note={plan.fourteen ? `Twice in ${monthName(plan.holidayMonth)} and ${monthName(plan.christmasMonth)}` : 'What reaches the bank'} />
        {plan.mealCard > 0 && <Line label="Meal card" value={<><Money value={plan.mealCard} /> a month</>} note="Not counted in savings" />}
        {!compact && later.length > 0 && <Line label="Changes later" value={later.map((x) => monthLabel(x.from)).join(', ')} />}
      </div>
    );
  }
  const una = (plan.deductions || []).filter((d) => d.enabled !== false && (d.pct || d.fixed));
  return (
    <div className="salary-lines">
      <Line label="Base salary" value={<><Money value={plan.base} /> a month</>} note="Paid 14 times a year: every month, plus June and December" />
      {plan.commission > 0 && <Line label="Commission" value={<><Money value={plan.commission} /> a month</>} note="When you reach 100% of your target" />}
      {plan.mealPerDay > 0 && plan.mealType !== 'none' && <Line label="Lunch allowance" value={<><Money value={plan.mealPerDay} /> a working day</>} note={plan.mealType === 'card' ? 'On the meal card · not counted in savings' : 'Paid with the salary'} />}
      {plan.allowance > 0 && (plan.allowanceType === 'reimbursement'
        ? <Line label={(plan.allowanceLabel || 'Expenses').replace(/\s*\(.*\)$/, '')} value={<>up to <Money value={plan.allowance} /> a month</>} note="Paid back when you claim expenses · not income" />
        : <Line label={plan.allowanceLabel || 'Allowance'} value={<><Money value={plan.allowance} /> a month</>} note="Paid with the salary" />)}
      {plan.yearlyBonus > 0 && <Line label={plan.yearlyBonusLabel || 'Yearly bonus'} value={<><Money value={plan.yearlyBonus} /> once a year</>} note={`Paid in ${monthName(plan.yearlyBonusMonth)}`} />}
      {!compact && <Line label="Social security" value={`${plan.ssRate}% of base salary${plan.commissionSS !== false && plan.commission ? ' and commission' : ''}`} note={plan.commissionSS === false && plan.commission ? 'Not on commission, as in the offer letter' : null} />}
      {una.map((d) => <Line key={d.id} label={d.label || 'Deduction'} value={d.pct ? `${fmtPlain(d.pct)}% of ${d.of === 'base' ? 'base salary' : d.of === 'gross' ? 'all pay' : 'commission'}${d.fixed ? ` + ${fmt(d.fixed)}` : ''}` : fmt(d.fixed)} note="Taken from pay" />)}
      {!compact && <Line label="Started" value={plan.startDate ? dateLabel(plan.startDate, { weekday: false }) : `pay counted from ${monthLabel(person.plans[0].from)}`} note={plan.startDate ? null : 'Add your first day at the job for a correct first month'} />}
      {!compact && later.length > 0 && <Line label="Raises planned" value={later.map((x) => monthLabel(x.from)).join(', ')} />}
    </div>
  );
}

/* ── Editor ────────────────────────────────────────────── */

// mode 'edit': correct the current figures · mode 'raise': new figures from a month on (raise, new job…)
export function SalaryDialog({ person, mode = 'edit', onClose }) {
  const { data, model, commit } = useApp();
  const currentPlan = versionAt(person.plans, model.current) || person.plans[person.plans.length - 1] || person.plans[0];
  const base = withPlanDefaults(currentPlan || { mode: 'employee', ...DEFAULT_EMPLOYEE_PLAN });
  const isFirst = currentPlan && person.plans[0]?.id === currentPlan.id;
  const [kind, setKind] = useState(base.mode === 'none' ? 'none' : base.mode);
  const [label, setLabel] = useState(base.label || '');
  const salary = useMoneyText(base.base || null, { allowNegative: false });
  const commission = useMoneyText(base.commission || null, { allowNegative: false });
  const [commissionSS, setCommissionSS] = useState(base.commissionSS !== false);
  const meal = useMoneyText(base.mealPerDay || null, { allowNegative: false });
  const [mealType, setMealType] = useState(base.mealType || 'card');
  const allowance = useMoneyText(base.allowance || null, { allowNegative: false });
  const [allowanceType, setAllowanceType] = useState(base.allowanceType || 'reimbursement');
  const [allowanceLabel] = useState(base.allowanceLabel || 'Expenses');
  const [deductions, setDeductions] = useState(() => (base.deductions || []).map((d) => ({ ...d, pctText: fmtPlain(d.pct || 0), fixedText: d.fixed ? fmtPlain(d.fixed) : '' })));
  const bonus = useMoneyText(base.yearlyBonus || null, { allowNegative: false });
  // The offer letter's figures belong to this salary: a raise starts without them
  const letterBase = useMoneyText(mode !== 'raise' && typeof base.letterIrsBase === 'number' ? base.letterIrsBase : null, { allowNegative: false });
  const letterCommission = useMoneyText(mode !== 'raise' && typeof base.letterIrsCommission === 'number' ? base.letterIrsCommission : null, { allowNegative: false });
  const [bonusMonth, setBonusMonth] = useState(base.yearlyBonusMonth || 12);
  const [startDate, setStartDate] = useState(base.startDate || '');
  const [endDate, setEndDate] = useState(base.endDate || '');
  const [sub, setSub] = useState(base.subsidies || DEFAULT_EMPLOYEE_PLAN.subsidies);
  const [vacationMonth, setVacationMonth] = useState(base.vacationMonth ?? 8);
  const [vacationDays, setVacationDays] = useState(String(base.vacationDays ?? 22));
  const [table, setTable] = useState(base.table || 'I');
  const [dependents, setDependents] = useState(String(base.dependents || 0));
  const [ssRate, setSsRate] = useState(String(base.ssRate ?? 11));
  const net = useMoneyText(base.mode === 'net' ? base.net : null, { allowNegative: false });
  const mealCard = useMoneyText(base.mode === 'net' ? base.mealCard || null : null, { allowNegative: false });
  const [fourteen, setFourteen] = useState(base.mode === 'net' ? !!base.fourteen : false);
  // a raise starts next month by default
  // a raise starts next month by default, and never on top of the latest salary change
  const latestFrom = person.plans.reduce((a, x) => (x.from > a ? x.from : a), data.settings.startMonth);
  const nextMonth = addMonths(model.current < data.settings.startMonth ? data.settings.startMonth : model.current, 1);
  const [from, setFrom] = useState(mode === 'raise' ? [nextMonth, addMonths(latestFrom, 1)].sort().pop() : currentPlan?.from || data.settings.startMonth);
  const [newJob, setNewJob] = useState(false);
  const [error, setError] = useState(null);

  const build = () => {
    if (kind === 'none') return { mode: 'none' };
    if (kind === 'net') {
      if (!net.parsed.ok || net.parsed.value === null) throw new Error('Enter the take-home pay');
      if (!mealCard.parsed.ok) throw new Error(mealCard.parsed.error);
      return { mode: 'net', net: net.parsed.value, mealCard: mealCard.parsed.value || 0, fourteen, holidayMonth: 6, christmasMonth: 12 };
    }
    for (const [m, n] of [[salary, 'Base salary'], [commission, 'Commission'], [meal, 'Lunch allowance'], [allowance, 'Expenses'], [bonus, 'Yearly bonus'], [letterBase, 'Offer letter IRS'], [letterCommission, 'Offer letter IRS']]) if (!m.parsed.ok) throw new Error(`${n}: ${m.parsed.error}`);
    if (salary.parsed.value === null) throw new Error('Enter the base salary');
    if (startDate && !isDate(startDate)) throw new Error('The first day at the job is not a valid date');
    const vd = parseMoney(vacationDays, { allowNegative: false });
    if (!vd.ok || vd.value > 60) throw new Error('Holiday days should be a number of days');
    const deps = Number(dependents);
    if (!Number.isInteger(deps) || deps < 0) throw new Error('Dependants must be a whole number');
    const ss = parsePercent(ssRate, { max: 50 });
    if (!ss.ok) throw new Error(`Social security: ${ss.error}`);
    const ds = deductions.map((d) => {
      const p = parsePercent(d.pctText || '0', { max: 100 });
      const f = parseMoney(d.fixedText || '0', { allowNegative: false });
      if (!p.ok || !f.ok) throw new Error(`"${d.label || 'Deduction'}": check the numbers`);
      return { id: d.id, label: (d.label || '').trim() || 'Deduction', pct: p.value, of: d.of, fixed: f.value || 0, enabled: d.enabled !== false };
    });
    return {
      ...(base.mode === 'employee' ? base : DEFAULT_EMPLOYEE_PLAN),
      mode: 'employee', label: label.trim(), base: salary.parsed.value, commission: commission.parsed.value || 0, commissionSS,
      mealPerDay: meal.parsed.value || 0, mealType, allowance: allowance.parsed.value || 0, allowanceType, allowanceLabel,
      allowanceTaxable: allowanceType === 'fixed' && !!base.allowanceTaxable, yearlyBonus: bonus.parsed.value || 0, yearlyBonusMonth: Number(bonusMonth),
      startDate: mode === 'raise' && !newJob ? base.startDate || '' : startDate, endDate,
      subsidies: { mode: sub.mode, holidayMonth: Number(sub.holidayMonth), christmasMonth: Number(sub.christmasMonth) },
      vacationMonth: Number(vacationMonth), vacationDays: vd.value, table, dependents: deps, ssRate: ss.value, deductions: ds,
      letterIrsBase: letterBase.parsed.value ?? undefined, letterIrsCommission: letterCommission.parsed.value ?? undefined,
      // the letter's printed yearly net only still applies if nothing it depends on changed
      letterNetYear: mode !== 'raise' && base.letterNetYear !== undefined && letterBase.parsed.value === base.letterIrsBase
        && letterCommission.parsed.value === base.letterIrsCommission && salary.parsed.value === base.base && (commission.parsed.value || 0) === base.commission
        ? base.letterNetYear : undefined,
    };
  };

  let draft = null;
  try { draft = build(); } catch { draft = null; }
  const previewFrom = mode === 'raise' ? from : (draft?.startDate && isDate(draft.startDate) ? monthOf(draft.startDate) : currentPlan?.from || model.current);
  const after = useMemo(() => (draft ? normalMonth(draft, person, data.taxYears, previewFrom) : null), [JSON.stringify(draft), previewFrom]); // eslint-disable-line react-hooks/exhaustive-deps
  const before = useMemo(() => (mode === 'raise' && currentPlan ? normalMonth(currentPlan, person, data.taxYears, from) : null), [mode, currentPlan, from]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    let next;
    try { next = build(); } catch (e) { setError(e.message); return; }
    let res;
    if (!currentPlan) {
      res = commit(`${person.name}: salary set`, (s) => setPlanFrom(s, person.id, data.settings.startMonth, next));
    } else if (mode === 'raise') {
      res = commit(`${person.name}: new salary from ${monthLabel(from)}`, (s) => setPlanFrom(s, person.id, from, { ...next, id: undefined }));
    } else {
      // For the first job, the first day decides from when pay counts
      const newFrom = isFirst && next.startDate && isDate(next.startDate) && monthOf(next.startDate) !== currentPlan.from && !person.plans.some((x) => x.id !== currentPlan.id && x.from === monthOf(next.startDate))
        ? monthOf(next.startDate) : currentPlan.from;
      res = commit(`${person.name}: salary updated`, (s) => updatePlan(s, person.id, currentPlan.id, { ...next, from: newFrom }));
    }
    if (res.error) setError(res.error);
    else onClose();
  };

  const updateDeduction = (id, patch) => setDeductions(deductions.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  return (
    <Dialog
      wide
      title={mode === 'raise' ? `${person.name}: new salary` : `${person.name}'s salary`}
      onClose={onClose}
      footer={(
        <>
          {after && (
            <span className="preview-pill" aria-live="polite">
              Normal month to the bank: {before && <><span className="muted"><Money value={before.net} /></span> → </>}<b><Money value={after.net} /></b>
            </span>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" onClick={submit}>Save</button>
        </>
      )}
    >
      {error && <div className="banner danger" role="alert"><Icon name="alert" className="icon" /><div>{error}</div></div>}
      {mode === 'raise' && (
        <div className="stack tight">
          <div className="row wrap" style={{ alignItems: 'flex-end' }}>
            <div style={{ minWidth: 280 }}><Field label="Starting in" group><MonthSelect ariaLabel="Starting in" value={from} onChange={(v) => v && setFrom(v)} /></Field></div>
            <Toggle checked={newJob} onChange={setNewJob} label="It's a new job" />
          </div>
          <p className="small muted">Months before keep the old salary, and paydays already confirmed never change. Only change what's different.</p>
        </div>
      )}

      {kind === 'employee' && (
        <div className="stack">
          <div className="grid2">
            <MoneyField label="Base salary, gross per month" hint="Vencimento base · paid 14 times a year" m={salary} autoFocus />
            <MoneyField label="Commission at 100% of target, per month" hint="Comissões · you can say how much you hit when reviewing each payday" m={commission} placeholder="0" />
            <Field label="Lunch allowance per working day" hint="Subsídio de almoço" group>
              <div className="row">
                <div style={{ flex: 1 }}><MoneyInput ariaLabel="Lunch allowance per working day" value={meal.text} parsed={meal.parsed} onChange={meal.setText} placeholder="0" /></div>
                <select className="input" style={{ width: 150 }} aria-label="Lunch allowance paid" value={mealType} onChange={(e) => setMealType(e.target.value)}>
                  <option value="card">on a meal card</option><option value="cash">with the salary</option><option value="none">not paid</option>
                </select>
              </div>
            </Field>
            <Field label="Expenses, up to per month" hint={allowanceType === 'reimbursement' ? 'Despesas · paid back when you claim them, so not income' : 'Paid every month with the salary'} group>
              <div className="row">
                <div style={{ flex: 1 }}><MoneyInput ariaLabel="Expenses, up to per month" value={allowance.text} parsed={allowance.parsed} onChange={allowance.setText} placeholder="0" /></div>
                <select className="input" style={{ width: 150 }} aria-label="Expenses kind" value={allowanceType} onChange={(e) => setAllowanceType(e.target.value)}>
                  <option value="reimbursement">paid back</option><option value="fixed">fixed allowance</option>
                </select>
              </div>
            </Field>
          </div>

          <Field label="Taken from your pay" hint="Like the UNA Seguros fee on commission" group>
            <div className="stack tight">
              {deductions.map((d, i) => (
                <div className="row wrap" key={d.id}>
                  <Toggle checked={d.enabled !== false} onChange={(v) => updateDeduction(d.id, { enabled: v })} label="" />
                  <input className="input" style={{ flex: 2, minWidth: 140 }} aria-label={`Taken from pay ${i + 1}: name`} value={d.label} onChange={(e) => updateDeduction(d.id, { label: e.target.value })} />
                  <div className="input-wrap" style={{ width: 90 }}><input className="input money" aria-label={`Taken from pay ${i + 1}: percent`} value={d.pctText} onChange={(e) => updateDeduction(d.id, { pctText: e.target.value })} /><span className="suffix">%</span></div>
                  <select className="input" style={{ width: 150 }} aria-label={`Taken from pay ${i + 1}: of`} value={d.of} onChange={(e) => updateDeduction(d.id, { of: e.target.value })}>
                    <option value="commission">of commission</option><option value="base">of base salary</option><option value="gross">of all pay</option>
                  </select>
                  <button type="button" className="btn ghost icon" aria-label={`Remove taken from pay ${i + 1}`} onClick={() => setDeductions(deductions.filter((x) => x.id !== d.id))}><Icon name="trash" /></button>
                </div>
              ))}
              <button type="button" className="linkbtn" style={{ alignSelf: 'flex-start' }} onClick={() => setDeductions([...deductions, { id: newId('d'), label: '', pctText: '0', fixedText: '', of: 'commission', enabled: true }])}>+ Add something taken from pay</button>
            </div>
          </Field>

          {mode === 'edit' && isFirst && (
            <Field label="First day at this job" hint="The first month is paid only from this day">
              <input className="input" type="date" style={{ maxWidth: 220 }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
          )}
          {mode === 'raise' && newJob && (
            <Field label="First day at the new job">
              <input className="input" type="date" style={{ maxWidth: 220 }} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
          )}

          <details className="more">
            <summary>More options (rarely needed)</summary>
            <div className="stack">
              <Toggle checked={commissionSS} onChange={setCommissionSS} label="Commission also pays social security (your offer letter says it doesn't)" />
              <div className="grid3">
                <MoneyField label="Yearly bonus" hint="Obj. Qual. · paid once a year" m={bonus} placeholder="0" />
                <Field label="Yearly bonus paid in"><select className="input" value={bonusMonth} onChange={(e) => setBonusMonth(Number(e.target.value))}>{monthOptions}</select></Field>
                <Field label="Employer"><input className="input" value={label} onChange={(e) => setLabel(e.target.value)} maxLength={60} placeholder="e.g. LAYER8" /></Field>
              </div>
              <div className="grid3">
                <Field label="Holiday pay (subsídio de férias) in"><select className="input" value={sub.holidayMonth} onChange={(e) => setSub({ ...sub, holidayMonth: Number(e.target.value) })}>{monthOptions}</select></Field>
                <Field label="Christmas pay (subsídio de Natal) in"><select className="input" value={sub.christmasMonth} onChange={(e) => setSub({ ...sub, christmasMonth: Number(e.target.value) })}>{monthOptions}</select></Field>
                <Field label="Those two are paid">
                  <select className="input" value={sub.mode} onChange={(e) => setSub({ ...sub, mode: e.target.value })}>
                    <option value="lump">all at once</option><option value="twelfths">a twelfth every month</option><option value="half">half at once, half monthly</option>
                  </select>
                </Field>
              </div>
              <div className="grid3">
                <Field label="Usual holidays in" hint="No lunch allowance on those days"><select className="input" value={vacationMonth} onChange={(e) => setVacationMonth(Number(e.target.value))}><option value={0}>don't assume any</option>{monthOptions}</select></Field>
                <Field label="Working days of holidays"><input className="input" inputMode="numeric" value={vacationDays} onChange={(e) => setVacationDays(e.target.value)} /></Field>
                <Field label="Contract ends (if it does)"><input className="input" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} /></Field>
              </div>
              <div className="grid3">
                <MoneyField label="Offer letter: IRS on base salary" hint="Only to compare with IRS Jovem on the Pay page" m={letterBase} placeholder="—" />
                <MoneyField label="Offer letter: IRS on commission" hint="“IRS Variável” on the letter" m={letterCommission} placeholder="—" />
              </div>
              <div className="grid3">
                <Field label="IRS situation">
                  <select className="input" value={table} onChange={(e) => setTable(e.target.value)}>{Object.entries(TABLE_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
                </Field>
                <Field label="Children / dependants"><input className="input" inputMode="numeric" value={dependents} onChange={(e) => setDependents(e.target.value)} /></Field>
                <Field label="Social security rate"><div className="input-wrap"><input className="input money" value={ssRate} onChange={(e) => setSsRate(e.target.value)} aria-label="Social security rate" /><span className="suffix">%</span></div></Field>
              </div>
            </div>
          </details>
        </div>
      )}

      {kind === 'net' && (
        <div className="stack">
          <div className="grid2">
            <MoneyField label="Take-home pay per month" hint="What reaches the bank" m={net} autoFocus />
            <MoneyField label="Meal card per month (if any)" m={mealCard} placeholder="0" />
          </div>
          <Toggle checked={fourteen} onChange={setFourteen} label="Paid twice in June and December (14 payments a year)" />
        </div>
      )}
      {kind === 'none' && <p className="dim">Nothing comes in (between jobs, studying…). The share of expenses still counts, so savings go down those months.</p>}

      <div className="row wrap small" style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
        <span className="muted">Kind of income:</span>
        {[['employee', 'Salary from a payslip'], ['net', 'Just the take-home amount'], ['none', 'No income']].map(([k, l]) => (
          <button key={k} type="button" className={`chip ${kind === k ? 'chip-on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(k)}>{l}</button>
        ))}
      </div>
    </Dialog>
  );
}

/* ── The person: name, sign-in email, birthday, payday ─── */

export function PersonDialog({ person, onClose }) {
  const { commit, model } = useApp();
  const [name, setName] = useState(person.name);
  const [color, setColor] = useState(person.color);
  const [email, setEmail] = useState(person.email || '');
  const [birthDate, setBirthDate] = useState(person.birthDate || '');
  const [pd, setPd] = useState(person.payday || { type: 'last-business-day' });
  const [dayText, setDayText] = useState(String(person.payday?.day || 25));
  const day = parseInt(dayText, 10);
  const pdFinal = pd.type === 'day' ? { ...pd, day } : pd;
  const [carnival, setCarnival] = useState(!!person.holidays?.carnival);
  const [extra, setExtra] = useState((person.holidays?.extra || []).join(', '));
  const [error, setError] = useState(null);
  const sample = pd.type === 'day' && !(day >= 1 && day <= 31) ? '—' : dateLabel(paydayOf(model.current, pdFinal, { carnival, extra: extra.split(',').map((x) => x.trim()).filter(Boolean) }));
  const submit = () => {
    const extraList = extra.split(',').map((x) => x.trim()).filter(Boolean);
    const bad = extraList.find((x) => !/^\d{2}-\d{2}$/.test(x) && !isDate(x));
    if (bad) { setError(`"${bad}" isn't a date: use 06-13 for every year or 2026-12-24 for one day`); return; }
    if (pd.type === 'day' && !(day >= 1 && day <= 31)) { setError('Payday must be a day from 1 to 31'); return; }
    const res = commit(`Edit ${name}`, (s) => updatePerson(s, person.id, { name, color, email, birthDate, payday: pdFinal, holidays: { carnival, extra: extraList } }));
    if (res.error) setError(res.error); else onClose();
  };
  return (
    <Dialog title={`About ${person.name}`} onClose={onClose} footer={<><span className="spacer" /><button type="button" className="btn" onClick={onClose}>Cancel</button><button type="button" className="btn primary" onClick={submit}>Save</button></>}>
      {error && <div className="banner danger" role="alert"><Icon name="alert" className="icon" />{error}</div>}
      <div className="grid2">
        <Field label="Name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={60} autoFocus /></Field>
        <Field label="Colour" group>
          <div className="row">{PERSON_COLORS.map((c) => <button key={c} type="button" aria-label={`Colour ${c}`} aria-pressed={color === c} onClick={() => setColor(c)} style={{ width: 24, height: 24, borderRadius: '50%', background: c, border: color === c ? '2px solid #fff' : '2px solid transparent' }} />)}</div>
        </Field>
      </div>
      <div className="grid2">
        <Field label="Google email" hint="The account used to sign in"><input className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@gmail.com" /></Field>
        <Field label="Birth date" hint="IRS Jovem stops the year you turn 36"><input className="input" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} /></Field>
      </div>
      <Field label="Payday" hint={`In ${monthLabel(model.current)}: ${sample}`} group>
        <div className="stack tight">
          <select className="input" aria-label="Payday" value={pd.type} onChange={(e) => setPd(e.target.value === 'day' ? { type: 'day', day: day || 25, shift: 'before', nextMonth: false } : { type: e.target.value })}>
            <option value="last-business-day">Last working day of the month</option>
            <option value="day">A fixed day</option>
            <option value="first-business-day-next">First working day of the next month</option>
          </select>
          {pd.type === 'day' && (
            <div className="row wrap">
              <span className="small dim">Day</span>
              <input className="input" style={{ width: 70 }} inputMode="numeric" aria-label="Day of the month" value={dayText} onChange={(e) => setDayText(e.target.value.replace(/\D/g, '').slice(0, 2))} />
              <select className="input" style={{ width: 'auto' }} aria-label="Which month" value={pd.nextMonth ? 'next' : 'same'} onChange={(e) => setPd({ ...pd, nextMonth: e.target.value === 'next' })}><option value="same">of the same month</option><option value="next">of the following month</option></select>
              <select className="input" style={{ width: 'auto' }} aria-label="On weekends and holidays" value={pd.shift || 'none'} onChange={(e) => setPd({ ...pd, shift: e.target.value })}><option value="before">earlier if not a working day</option><option value="after">later if not a working day</option><option value="none">even on weekends</option></select>
            </div>
          )}
        </div>
      </Field>
      <details className="more">
        <summary>Days off besides national holidays</summary>
        <div className="stack tight">
          <Toggle checked={carnival} onChange={setCarnival} label="Carnival is a day off" />
          <Field label="Local holiday or other days off" hint="06-13 for Lisbon, 06-24 for Porto (every year), or a date like 2026-12-24">
            <input className="input" value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="06-13, 2026-12-24" />
          </Field>
        </div>
      </details>
    </Dialog>
  );
}

export const paydayText = (person) => paydayRuleLabel(person.payday);
export { PersonDot };
